import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Warning as WarningIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

export type ConfirmDialogVariant = 'danger' | 'warning' | 'info';

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  message: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmDialogVariant;
  isLoading?: boolean;
}

const variantConfig: Record<
  ConfirmDialogVariant,
  {
    icon: React.ReactNode;
    color: 'error' | 'warning' | 'info';
    iconBgColor: string;
    iconColor: string;
  }
> = {
  danger: {
    icon: <DeleteIcon />,
    color: 'error',
    iconBgColor: 'var(--color-error-50, #FEE2E2)',
    iconColor: 'var(--color-error-600, #DC2626)',
  },
  warning: {
    icon: <WarningIcon />,
    color: 'warning',
    iconBgColor: 'var(--color-warning-50, #FFFBEB)',
    iconColor: 'var(--color-warning-600, #D97706)',
  },
  info: {
    icon: <InfoIcon />,
    color: 'info',
    iconBgColor: 'var(--color-info-50, #EFF6FF)',
    iconColor: 'var(--color-info-600, #2563EB)',
  },
};

const CONTROLS_HANDLING_ENTER = 'button, a[href], input, select, textarea, [role="button"]';

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  isLoading: externalLoading,
}) => {
  const [internalLoading, setInternalLoading] = useState(false);
  const isLoading = externalLoading ?? internalLoading;

  const config = variantConfig[variant];

  const handleConfirm = useCallback(async () => {
    setInternalLoading(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      // Error is expected to be handled by caller
      // Don't close dialog on error so user can retry
    } finally {
      setInternalLoading(false);
    }
  }, [onConfirm, onClose]);

  const handleClose = useCallback(() => {
    if (isLoading) return;
    onClose();
  }, [isLoading, onClose]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key !== 'Enter' || isLoading) return;
      // A focused control handles Enter itself, so Enter on Cancel or the close icon cancels.
      if ((event.target as HTMLElement).closest(CONTROLS_HANDLING_ENTER)) return;

      event.preventDefault();
      handleConfirm();
    },
    [handleConfirm, isLoading],
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      onKeyDown={handleKeyDown}
      maxWidth="xs"
      fullWidth
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
    >
      <DialogTitle
        id="confirm-dialog-title"
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: config.iconBgColor,
              color: config.iconColor,
              flexShrink: 0,
            }}
          >
            {config.icon}
          </div>
          <Typography variant="h6" component="span" fontWeight={600}>
            {title}
          </Typography>
        </Stack>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={isLoading}
          size="small"
          sx={{ ml: 1 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <DialogContentText id="confirm-dialog-description" sx={{ color: 'text.secondary' }}>
          {message}
        </DialogContentText>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={isLoading} variant="outlined" color="inherit">
          {cancelText}
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={isLoading}
          variant="contained"
          color={config.color}
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isLoading ? 'Processing...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
